// frontend/src/pages/Home.tsx

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, TrendingUp, Lock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useEffect, useState } from 'react';
import api from '@/lib/api';

export function Home() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    users: 0,
    volume: 0,
    trades: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/stats');
        if (response.data?.stats) {
          setStats(response.data.stats);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, []);

  const features = [
    {
      icon: <TrendingUp className="w-8 h-8 text-apple-green-500" />,
      title: "Dynamic Spread Automation",
      description: "Maker orders automatically adjust with BTC price movements. Your spreads slide with volatility, protecting you from market swings while staying competitive.",
    },
    {
      icon: <Zap className="w-8 h-8 text-apple-blue-500" />,
      title: t('features.atomic_swaps.title'),
      description: t('features.atomic_swaps.description'),
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-apple-green-500" />,
      title: t('features.lightning_fast.title'),
      description: t('features.lightning_fast.description'),
    },
    {
      icon: <Lock className="w-8 h-8 text-apple-orange-500" />,
      title: t('features.non_custodial.title'),
      description: t('features.non_custodial.description'),
    },
    {
      icon: <Shield className="w-8 h-8 text-apple-blue-500" />,
      title: t('features.low_fees.title'),
      description: "1% buyer (Taker) and 0% seller (Maker). Transparent pricing with no hidden charges. Minimum fee is 100 sats. Makers provide liquidity for free, takers pay minimal fees.",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-apple-blue-50 to-white py-20 lg:py-32">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in">
              {t('hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-apple-gray-600 mb-8 animate-slide-up">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
              <Link to="/trade">
                <Button variant="primary" size="lg" icon={<ArrowRight className="w-5 h-5" />}>
                  {t('hero.cta_primary')}
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="secondary" size="lg">
                  {t('hero.cta_secondary')}
                </Button>
              </Link>
            </div>

            {/* Stats - Real data from API */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 animate-fade-in">
              <div>
                <div className="text-4xl font-bold text-apple-blue-500 mb-2">
                  {stats.users > 0 ? stats.users.toLocaleString() : '—'}
                </div>
                <div className="text-apple-gray-600">{t('hero.stats.users')}</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-apple-blue-500 mb-2">
                  {stats.volume > 0 ? `${Math.round(stats.volume * 100000000).toLocaleString()} sats` : '—'}
                </div>
                <div className="text-apple-gray-600">{t('hero.stats.total_volume', 'Total Volume')}</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-apple-blue-500 mb-2">
                  {stats.trades > 0 ? stats.trades.toLocaleString() : '—'}
                </div>
                <div className="text-apple-gray-600">{t('hero.stats.trades')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-0 right-0 -z-10 opacity-10">
          <Zap className="w-96 h-96 text-apple-blue-500" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {t('features.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center animate-slide-up">
                <CardContent className="pt-6">
                  <div className="flex justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-apple-gray-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section - replaces the cross-chain Ethereum section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white overflow-hidden">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-full px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 text-sm font-medium">Powered by Taproot Assets</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Swap BTC ↔ Stablecoins Instantly
              </h2>
              <p className="text-xl text-gray-300 mb-6">
                Convert between Bitcoin and USD stablecoins over the Lightning Network. No KYC, no middleman, instant settlement.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">USDT-TA &amp; USDC-TA</p>
                    <p className="text-gray-400 text-sm">Trade real USD stablecoins issued on Bitcoin via Taproot Assets</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Lock className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">Non-Custodial</p>
                    <p className="text-gray-400 text-sm">Your keys, your crypto. We never hold your funds.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Zap className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">Lightning Fast</p>
                    <p className="text-gray-400 text-sm">Atomic swaps settle in milliseconds over Lightning channels</p>
                  </div>
                </div>
              </div>
              <Link to="/trade">
                <Button variant="primary" size="lg" icon={<ArrowRight className="w-5 h-5" />}>
                  Start Trading
                </Button>
              </Link>
            </div>
            <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <div className="text-sm text-gray-400">You Send</div>
                <div className="text-sm text-gray-400">You Receive</div>
              </div>
              <div className="flex items-center justify-between mb-6">
                <div className="bg-gray-700 rounded-xl p-4 flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">₿</span>
                    </div>
                    <span className="font-semibold">BTC</span>
                  </div>
                  <p className="text-2xl font-bold">50,000 sats</p>
                </div>
                <div className="px-4">
                  <ArrowRight className="w-6 h-6 text-blue-400" />
                </div>
                <div className="bg-gray-700 rounded-xl p-4 flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">$</span>
                    </div>
                    <span className="font-semibold">USDT-TA</span>
                  </div>
                  <p className="text-2xl font-bold text-green-400">$50.00</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-400">
                  <span>Network Fee</span>
                  <span className="text-green-400 font-medium">Near Zero</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Settlement</span>
                  <span>Instant (Lightning)</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Custody</span>
                  <span>Non-custodial</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>KYC Required</span>
                  <span className="text-green-400">None</span>
                </div>
              </div>
              <div className="mt-6 bg-blue-500 bg-opacity-10 border border-blue-500 border-opacity-20 rounded-xl p-3 text-center">
                <p className="text-blue-400 text-sm">Powered by Lightning Network + Taproot Assets</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-apple-blue-500 to-apple-blue-600 text-white">
        <div className="container-main text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Trading?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Swap BTC and stablecoins on the Lightning Network
          </p>
          <Link to="/signup">
            <Button
              variant="secondary"
              size="lg"
              className="bg-white text-apple-blue-600 hover:bg-apple-gray-100"
            >
              Create Free Account
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
